import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { FESTIVAL_LANE, FESTIVAL_DECK_TIERS } from '@gjl/voice/pricing'

export default function FestivalRetainerPage() {
  const breadcrumbs = generateBreadcrumbs('/services/festival-retainer', breadcrumbLabels)

  const festivalPlatform = FESTIVAL_LANE.find((t) => t.id === 'festival-platform')!
  const festivalConcierge = FESTIVAL_LANE.find((t) => t.id === 'festival-concierge')!
  const projectAddOns = FESTIVAL_DECK_TIERS.find((t) => t.name === 'Project add-ons')!

  useSEO({
    title: 'Festival Digital Growth Retainer | Year-Round Support',
    description:
      'Year-round digital support for classical music festivals. Website updates, email campaigns, analytics, and strategy aligned with your recruitment cycle.',
    url: '/services/festival-retainer',
    canonical: '/services/festival-retainer',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Festival Digital Growth Retainer',
      description:
        'Ongoing digital support for classical music festivals including website updates, email campaigns, and strategic consulting.',
      url: '/services/festival-retainer',
    }),
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const tiersRef = useStaggeredGrid<HTMLDivElement>('.tier-card', { stagger: 0.12 })
  const includedRef = useScrollReveal<HTMLDivElement>('.included-item', { stagger: 0.08 })
  const timelineRef = useScrollReveal<HTMLDivElement>('.timeline-item', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Year-Round Digital Support for Your Festival</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your festival doesn&apos;t stop after launch season. You need email campaigns during application season.
          Website updates throughout the year. Analytics to track enrollment. SEO improvements for next year&apos;s
          recruitment.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          The Festival Platform plan gives you ongoing support tailored to your festival cycle, so you can focus on
          creating transformative musical experiences while we handle your digital strategy.
        </p>
        <p className="text-fluid-body text-foreground mx-auto max-w-2xl font-medium">
          Think of us as an extension of your team, someone who understands both the music and the tech.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Schedule a Strategy Call →
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Festivals Need Year-Round Digital Support</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              problem: 'Seasonal Recruitment Cycles',
              description:
                'Your digital needs change throughout the year. Application season requires email nurturing. Post-season needs website updates and enrollment analysis. You need someone who understands this rhythm.',
            },
            {
              problem: 'Ongoing Website Updates',
              description:
                'Faculty changes. Programs evolve. Schedules shift. You need someone who can update your website quickly without waiting weeks for a developer to respond.',
            },
            {
              problem: 'Email Campaigns During Application Season',
              description:
                "Inquiry nurture emails. Application reminders. Enrollment confirmations. Alumni updates. These don't write and send themselves. You need consistent campaign management.",
            },
            {
              problem: 'No Time for SEO or Analytics',
              description:
                'You know you should be tracking website traffic, application sources, and enrollment metrics. But who has time? You need someone monitoring this continuously.',
            },
            {
              problem: 'Technical Issues & Bug Fixes',
              description:
                "Forms break. Integrations stop working. Emails bounce. When tech issues happen mid-application season, you can't wait days for a fix. You need immediate support.",
            },
            {
              problem: 'No Strategic Partner',
              description:
                "You need someone who understands your festival's goals, tracks progress year over year, and proactively suggests improvements. Not just a vendor who builds once and disappears.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-4 text-center italic">
          A one-time website build isn&apos;t enough. Festivals need ongoing digital support aligned with their
          recruitment and operations cycle.
        </p>
      </section>

      {/* Festival Plans Section */}
      <section className="mx-auto max-w-5xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">Two Plans Plus Project Add-Ons</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            The short version: $500 a month if you have a designer, $1,000 a month if you would rather not hire one.
            Every plan includes the underlying software.
          </p>
        </div>

        <div ref={tiersRef} className="grid gap-6 md:grid-cols-3">
          {/* Festival Platform */}
          <motion.div
            className="tier-card border-primary bg-primary/5 space-y-4 rounded-lg border-2 p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary text-xs font-semibold tracking-wider uppercase">Most Popular</div>
            <div>
              <h3 className="text-xl font-semibold text-white">{festivalPlatform.name}</h3>
              <p className="text-primary mt-1 text-lg font-bold">{festivalPlatform.price}</p>
              <p className="text-muted-foreground mt-2 text-sm">{festivalPlatform.note}</p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Festival website with hosting & editor',
                'Application forms built for auditions',
                'Email marketing for applicants & alumni',
                'Ticketing with QR check-in',
                'Light-managed: we keep it all running',
                'Best fit if you have your own designer',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Festival Concierge */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">{festivalConcierge.name}</h3>
              <p className="text-primary mt-1 text-lg font-bold">{festivalConcierge.price}</p>
              <p className="text-muted-foreground mt-2 text-sm">{festivalConcierge.note}</p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Everything in Festival Platform',
                'Branding & a design retainer',
                'Done-for-you launch & content',
                'Email campaigns written & sent for you',
                'Season-long strategy',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Project Add-Ons */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">{projectAddOns.name}</h3>
              <p className="text-primary mt-1 text-lg font-bold">{projectAddOns.price} one-time</p>
              <p className="text-muted-foreground mt-2 text-sm">{projectAddOns.line}</p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Logo design or brand refresh',
                'Print programs',
                'Season campaign creative',
                'Scoped & quoted up front',
                'Available on either plan',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="space-y-3 text-center">
          <p className="text-muted-foreground mx-auto max-w-3xl text-sm">
            Both plans start with a one-time onboarding (the $250 Launch build counts as it) and a 3-month minimum term,
            then run month to month. Leaving is always free, and you take a full export of your data with you.
          </p>
          <p className="text-muted-foreground mx-auto max-w-3xl text-sm">
            Want a single managed service instead of the full festival stack? See our{' '}
            <Link to="/services/done-for-you" className="text-primary hover:underline">
              Done-For-You Services
            </Link>{' '}
            page.
          </p>
        </div>
      </section>

      {/* What's Included Across All Tiers */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">What&apos;s Included in Both Plans</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center">
          Core services that every festival plan includes.
        </p>

        <div ref={includedRef} className="space-y-6">
          {[
            {
              title: 'Festival Cycle Planning',
              description:
                'We align digital strategy with your recruitment timeline, from summer planning to application season to enrollment close.',
            },
            {
              title: 'Ongoing Website Updates',
              description:
                'Program changes, faculty updates, schedule adjustments, and content refreshes handled promptly.',
            },
            {
              title: 'Technical Maintenance',
              description:
                'Hosting, security updates, backups, SSL management, and performance monitoring, all managed for you.',
            },
            {
              title: 'Analytics & Reporting',
              description:
                "Track website traffic, application funnel metrics, and enrollment data to understand what's working.",
            },
            {
              title: 'Strategic Guidance',
              description:
                "Regular consultation on digital strategy, enrollment optimization, and tech improvements tailored to your festival's goals.",
            },
            {
              title: 'Direct Access',
              description:
                'No ticketing systems or endless back-and-forth. Email, call, or Slack directly with someone who knows your festival.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="included-item border-primary bg-background/50 rounded-lg border p-5 backdrop-blur-sm"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">How Your Plan Works</h2>
        <div ref={timelineRef} className="space-y-6">
          {[
            {
              month: 'January-March: Planning Season',
              description:
                'We help plan your summer recruitment strategy, update website content for the new season, and prepare email campaigns for application launch.',
            },
            {
              month: 'April-May: Pre-Season Push',
              description:
                'Email campaigns to nurture inquiries, SEO improvements to rank for summer program searches, last-minute website updates before applications open.',
            },
            {
              month: 'June-August: Application Season',
              description:
                'Automated email sequences nurturing applicants, funnel optimization to reduce drop-offs, real-time analytics tracking, technical support for any issues.',
            },
            {
              month: 'September-November: Post-Season Review',
              description:
                "Analyze enrollment data, review what worked and what didn't, plan improvements for next year, update website with new faculty/programs.",
            },
            {
              month: 'December: Strategic Planning',
              description:
                'Year-end strategy session, set digital goals for next year, prepare budget for additional projects if needed.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="timeline-item border-primary space-y-2 border-l-4 py-3 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.month}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground pt-4 text-center italic">
          Your plan adapts to your festival&apos;s seasonal rhythm, not a generic monthly checklist.
        </p>
      </section>

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Festivals Choose Year-Round Support</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              benefit: 'Predictable Monthly Investment',
              description:
                'No surprise invoices. One monthly price you can plan around. After the 3-month minimum, you are month to month.',
            },
            {
              benefit: 'Continuous Improvement',
              description:
                'We track metrics year over year and continuously optimize your funnel, website, and campaigns for better enrollment.',
            },
            {
              benefit: 'Faster Response Times',
              description:
                'Festival clients get priority support. Urgent bug fixes come first, especially mid-application season. No waiting weeks for availability.',
            },
            {
              benefit: 'Proactive Strategy',
              description:
                "We don't wait for you to ask. We proactively suggest improvements, identify opportunities, and keep you ahead of the curve.",
            },
            {
              benefit: 'Deep Festival Knowledge',
              description:
                'After a few months, we know your programs, your audience, your goals. We become true partners, not just vendors.',
            },
            {
              benefit: 'Enrollment Growth Over Time',
              description:
                'Small optimizations compound. Email campaigns improve. SEO rankings increase. Funnel conversion rates rise. Enrollment grows year over year.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="benefit-card border-primary/20 bg-primary/5 space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.benefit}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'Can we start with Festival Platform and upgrade later?',
              answer:
                'Yes. Many festivals start on Festival Platform and move to Festival Concierge when they want branding, design, and content handled too. You can adjust your plan anytime.',
            },
            {
              question: 'What are the terms?',
              answer:
                'Both plans start with a one-time onboarding (the $250 Launch build counts as it) and a 3-month minimum term. After that you are month to month. Leaving is always free, and you get a full export of your data.',
            },
            {
              question: 'Do we need to have our website built by you first?',
              answer:
                'Not necessarily. We can take over a website built elsewhere after a one-time onboarding. If we build your site, the $250 Launch build counts as your onboarding.',
            },
            {
              question: 'How do email campaigns work?',
              answer:
                'On Festival Concierge, we write, design, and schedule email campaigns aligned with your festival calendar. You review and approve before sending. On Festival Platform, you get the email software and we keep it running.',
            },
            {
              question: 'Can we request additional projects during the plan?',
              answer: `Yes. One-time project add-ons like a logo, brand refresh, print programs, or a season campaign run ${projectAddOns.price}. Anything larger is scoped and quoted up front.`,
            },
            {
              question: 'How is this different from hiring a full-time marketing person?',
              answer:
                "You get specialized festival website + digital marketing expertise for a fraction of the cost of a full-time hire. Plus, we bring technical skills (development, integrations, analytics) that most marketing staff don't have.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Grow Your Festival&apos;s Digital Presence?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute strategy call to discuss your festival&apos;s needs and find the right plan for
            your goals.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Schedule Your Strategy Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No sales pitch. Just an honest conversation about how ongoing support can help your festival grow.
          </p>
        </div>
      </section>
    </div>
  )
}
